import React from "react";
import { useRouter } from "next/router";
import { Formik, Field, Form } from "formik";
import { object, string, number } from "yup";
import { Input } from "../../components/Input";
import Button from "../../components/Button";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Text,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  Image as ChakraImage,
  Icon,
} from "@chakra-ui/react";
import { FaEdit } from "react-icons/fa";
import { Dialog } from "../../components/Dialog";
import { CapyAware } from "../../assets";
import Loader from "../../components/Loader";
import useDonation from "../../hooks/useDonations";
import { useMiniGameOrder } from "../../hooks/useMiniGameOrder";
import {
  formatMoney,
  getMinTipIDR,
  getMaxTipIDR,
  getMinTipPHP,
  getMaxTipPHP,
  getMinTipJPY,
  getMaxTipJPY,
} from "../../utils/";
import { Large, P } from "../../components/Type";
import Link from "../../components/Link";
import { UsernameBadge } from "../../components/Username";
import isEmail from "validator/lib/isEmail";
import { Trans, msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { PaymentMethods } from "../PaymentMethods";
import { getFormError } from "../../i18n";
import {
  PaymentMethodSelectorProvider,
  usePaymentMethodSelector,
} from "../../contexts/paymentMethodSelector";

// Same base validation as DonationForm/WishlistForm, minus media/vote/mabar.
const baseYupModel = {
  from: string()
    .required(msg`Nama tidak boleh kosong`)
    .max(32, msg`Nama terlalu panjang`)
    .test(
      "not-readable-domain",
      () => msg`Nama tidak diperbolehkan`,
      async (value = "") => await !isReadableAsDomain(value)
    ),
  message: string().required(msg`Pesan tidak boleh kosong`).max(140),
  email: string()
    .required(msg`Masukkan email kamu`)
    .email(msg`Masukan email kamu`)
    .test(
      "is-valid",
      () => msg`Periksa lagi email kamu`,
      async (value = "") => await isEmail(value)
    ),
  phone: string(),
};

// amount is the streamer's fixed ticket price, still validated against the tip range.
const getYupSchema = () =>
  object().shape({
    ...baseYupModel,
    amount: number().required(),
  });

function isReadableAsDomain(str) {
  const noSpaces = str.replace(/\s/g, "");
  if (noSpaces.indexOf("http") >= 0 || noSpaces.indexOf(":/") >= 0) return true;
  const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}$/;
  return domainRegex.test(noSpaces.toLowerCase());
}

const phoneRegex = new RegExp("^08\\d{7,11}$");

const getGatewayTipRange = (currency, gateway) => {
  switch (currency) {
    case "PHP":
      return { min: getMinTipPHP(gateway), max: getMaxTipPHP(gateway) };
    case "JPY":
      return { min: getMinTipJPY(gateway), max: getMaxTipJPY(gateway) };
    case "IDR":
    default:
      return { min: getMinTipIDR(gateway), max: getMaxTipIDR(gateway) };
  }
};

const isAmountWithinGatewayRange = ({ currency, gateway, amount }) => {
  if (!gateway) return true;
  const n = Number(amount);
  if (!Number.isFinite(n)) return false;
  const { min, max } = getGatewayTipRange(currency, gateway);
  return n >= min && n <= max;
};

// Mini Games ticket form: Nominal is the streamer's ticket price (not editable). Everything
// else (Dari/Email/Pesan/checkboxes/payment) mirrors the streamer DonationForm.
const MiniGameBuyForm = ({
  amount, // ticket price
  streamerId,
  streamerUsername = "",
  slug = "",
  isGopayEnabled,
  isOvoEnabled,
  isDanaEnabled,
  isLinkAjaEnabled,
  isQRISEnabled,
  isBCAVAEnabled,
  isMandiriVAEnabled,
  isBRIVAEnabled,
  isBNIVAEnabled,
  isPermataVAEnabled,
  isBJBVAEnabled,
  isBSIVAEnabled,
  isCIMBVAEnabled,
  currUsername = "",
  currEmail = "",
  currency,
  transactionFeePolicy = null,
}) => {
  const { calculateAmountToPay } = useDonation();
  const { createOrder } = useMiniGameOrder();
  const { _ } = useLingui();
  const yupSchema = getYupSchema();

  return (
    <PaymentMethodSelectorProvider>
      <Formik
        validationSchema={yupSchema}
        initialValues={{
          agree: false,
          notUnderage: false,
          from: currUsername,
          message: "",
          amount,
          payment_type: "",
          email: currEmail,
          phone: "",
          amountToPay: "",
          currency,
          pgFee: "",
          platformFee: "",
        }}
        enableReinitialize
        validate={async (val) => {
          const error = {};
          if (val.payment_type === "ovo") {
            if (!phoneRegex.test(val.phone)) {
              error["phone"] = msg`Format nomor telepon salah. Gunakan nomor lainnya`;
            }
            if (!val.phone) {
              error["phone"] = msg`Masukkan nomor telepon untuk pembayaran ${val.payment_type.toUpperCase()}`;
            }
          }
          if (val.payment_type === "qris" && val.amountToPay > 10_000_000) {
            error["payment_type"] = msg`Maksimal total pembayaran menggunakan QRIS adalah Rp10.000.000`;
          }
          if (!val.notUnderage) {
            error["notUnderage"] = msg`Anda harus berusia 17 tahun atau lebih.`;
          }
          if (!val.agree) {
            error["agree"] = msg`Anda harus menyetujui syarat dan ketentuan saweria.co`;
          }
          return error;
        }}
        onSubmit={() => {}}
      >
        {({ setFieldValue, submitForm, isSubmitting, setSubmitting, values, setFieldError, errors }) => {
          const router = useRouter();
          // Guests (not logged in) get an email-confirmation step before paying,
          // since the ticket code and room link are sent to that email. The email is
          // editable in-place, reusing the form's email field + validation.
          const [emailConfirmOpen, setEmailConfirmOpen] = React.useState(false);
          const [editingEmail, setEditingEmail] = React.useState(false);

          const placeOrder = async () => {
            setSubmitting(true);
            try {
              const { amountToPay, pgFee, platformFee, ...valueRes } = values;
              await createOrder(streamerUsername, slug, valueRes);
            } catch (error) {
              console.log(error);
            }
            setSubmitting(false);
          };

          const handleBuy = () => {
            if (!currUsername) {
              setEditingEmail(false);
              setEmailConfirmOpen(true);
              return;
            }
            placeOrder();
          };

          const handleConfirmEmail = () => {
            setEmailConfirmOpen(false);
            placeOrder();
          };
          const {
            selectedPaymentMethod,
            setSelectedPaymentMethod,
            setTransactionFeePolicy,
            setTransactionAmount,
            setPgFee,
          } = usePaymentMethodSelector();
          setTransactionFeePolicy(transactionFeePolicy);
          setTransactionAmount(values.amount);
          setPgFee(values.pgFee);

          const onClickPaymentMethod = (type) => async () => {
            const { min, max } = getGatewayTipRange(currency, type);
            const amountValue = Number(values.amount);
            if (!Number.isFinite(amountValue) || amountValue < min || amountValue > max) {
              setFieldError(
                "payment_type",
                msg`Nominal harus di antara ${formatMoney(min, { currency })} - ${formatMoney(max, { currency })} untuk metode pembayaran ini`
              );
              return;
            }
            setFieldError("payment_type", undefined);
            setSelectedPaymentMethod(type);
            await setFieldValue("payment_type", type, false);

            const { username } = router.query;
            const amountToPayRes = await calculateAmountToPay(username, {
              ...values,
              payment_type: type,
            });
            await setFieldValue("amountToPay", amountToPayRes.amount_to_pay);
            await setFieldValue("pgFee", amountToPayRes.pg_fee);
            await setFieldValue("platformFee", amountToPayRes.platform_fee);
          };

          if (isSubmitting) return <Loader />;

          const amountNumber = Number(values.amount);
          const isCoreFormFilled =
            Boolean(values.from?.trim()) &&
            Boolean(values.message?.trim()) &&
            Boolean(values.email?.trim()) &&
            Boolean(values.notUnderage) &&
            Boolean(values.agree);

          const isGatewayOutOfRange = (gateway) =>
            isCoreFormFilled &&
            !isAmountWithinGatewayRange({ currency, gateway, amount: amountNumber });

          const disabledPayments = {
            qris: isGatewayOutOfRange("qris"),
            gopay: isGatewayOutOfRange("gopay"),
            ovo: isGatewayOutOfRange("ovo"),
            dana: isGatewayOutOfRange("dana"),
            linkaja: isGatewayOutOfRange("linkaja"),
            bca: isGatewayOutOfRange("bca"),
            mandiri: isGatewayOutOfRange("mandiri"),
            bri: isGatewayOutOfRange("bri"),
            bni: isGatewayOutOfRange("bni"),
            permata: isGatewayOutOfRange("permata"),
            bjb: isGatewayOutOfRange("bjb"),
            bsi: isGatewayOutOfRange("bsi"),
            cimb: isGatewayOutOfRange("cimb"),
            shopeepay: isGatewayOutOfRange("shopeepay"),
            grabpay: isGatewayOutOfRange("grabpay"),
            gcash: isGatewayOutOfRange("gcash"),
            paymaya: isGatewayOutOfRange("paymaya"),
            paypay: isGatewayOutOfRange("paypay"),
            alipay: isGatewayOutOfRange("alipay"),
            linepay: isGatewayOutOfRange("linepay"),
            rakutenpay: isGatewayOutOfRange("rakutenpay"),
            merpay: isGatewayOutOfRange("merpay"),
            inter_cc: isGatewayOutOfRange("inter_cc"),
            wechat_pay: isGatewayOutOfRange("wechat_pay"),
          };

          const backendEnabledPayments = {
            isDanaEnabled,
            isQRISEnabled,
            isGopayEnabled,
            isLinkAjaEnabled,
            isOvoEnabled,
            isBCAVAEnabled,
            isMandiriVAEnabled,
            isBRIVAEnabled,
            isBNIVAEnabled,
            isPermataVAEnabled,
            isBJBVAEnabled,
            isBSIVAEnabled,
            isCIMBVAEnabled,
          };

          const selectedGateway = values.payment_type || selectedPaymentMethod;
          const isSelectedPaymentAmountValid = selectedGateway ? !disabledPayments[selectedGateway] : true;
          const allowPayment = !Object.keys(errors).length && values.agree && isSelectedPaymentAmountValid;

          return (
            <Form>
              <FormControl isRequired mb={3}>
                <FormLabel htmlFor="amount"><Trans>Nominal:</Trans></FormLabel>
                <Text id="amount" color="black" fontSize="16px" fontWeight="700" lineHeight="24px" py="8px">
                  {formatMoney(values.amount, { currency })}
                </Text>
              </FormControl>

              <Field type="text" name="from">
                {({ field, form }) => (
                  <FormControl isRequired isReadOnly={currUsername} mb={3} isInvalid={form.errors.from && form.touched.from}>
                    <FormLabel htmlFor="from"><Trans>Dari:</Trans></FormLabel>
                    {currUsername ? (
                      <UsernameBadge w="min-content" username={currUsername} />
                    ) : (
                      <Input {...field} data-testid="from-input" id="from" placeholder={_(msg`Budi`)} />
                    )}
                    <FormErrorMessage>{getFormError(form.errors.from, _)}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="email">
                {({ field, form }) => (
                  <FormControl display={currEmail ? "none" : "block"} isRequired isReadOnly={currEmail} mb={3} isInvalid={form.errors.email && form.touched.email}>
                    <FormLabel htmlFor="email"><Trans>Email:</Trans></FormLabel>
                    <Input {...field} data-testid="email-input" type="email" id="email" placeholder={_(msg`budi@saweria.co`)} />
                    <FormErrorMessage>{getFormError(form.errors.email, _)}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field type="text" name="message">
                {({ field, form }) => (
                  <FormControl isRequired mb={3} isInvalid={form.errors.message && form.touched.message}>
                    <FormLabel htmlFor="message"><Trans>Pesan:</Trans></FormLabel>
                    <Input {...field} data-testid="message-input" id="message" placeholder={_(msg`Selamat pagi`)} />
                    <FormErrorMessage>{getFormError(form.errors.message, _)}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              {["ovo"].includes(values.payment_type) ? (
                <Field name="phone">
                  {({ field, form }) => (
                    <FormControl isRequired mb={3} isInvalid={form.errors.phone && form.touched.phone}>
                      <FormLabel htmlFor="phone"><Trans>Nomor telepon:</Trans></FormLabel>
                      <Input {...field} type="tel" data-testid="phone-input" id="phone" placeholder={_(msg`Nomor yang terdaftar di e-wallet pilihan`)} />
                      <FormErrorMessage>{getFormError(form.errors.phone, _)}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              ) : null}

              <Field name="notUnderage">
                {({ field, form }) => (
                  <FormControl isRequired mb="3" isInvalid={form.errors.notUnderage && form.touched.notUnderage}>
                    <Checkbox data-testid="underage-checkbox" size="lg" {...field} isInvalid={form.errors.notUnderage && form.touched.notUnderage} borderColor="black" isChecked={form.values.notUnderage}>
                      <P fontSize="0.75em" mb={0}><Trans>Saya berusia 17 tahun atau lebih</Trans></P>
                    </Checkbox>
                    <FormErrorMessage>{getFormError(form.errors.notUnderage, _)}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="agree">
                {({ field, form }) => (
                  <FormControl isRequired mb="3" isInvalid={form.errors.agree && form.touched.agree}>
                    <Checkbox data-testid="agree-checkbox" size="lg" {...field} isInvalid={form.errors.agree && form.touched.agree} borderColor="black" isChecked={form.values.agree}>
                      <P fontSize="0.75em" mb={0}>
                        <Trans>
                          Saya memahami dan menyetujui bahwa dukungan ini saya berikan secara sukarela untuk mendukung kreator, tidak dapat dikembalikan, bukan transaksi komersial, tidak digunakan untuk aktivitas ilegal, serta telah sesuai dengan{" "}
                          <Link textDecoration="underline" href="/terms">{`syarat dan ketentuan`}</Link>{" "}&{" "}
                          <Link textDecoration="underline" href="/privacy">kebijakan privasi</Link>{" "}saweria.co
                        </Trans>
                      </P>
                    </Checkbox>
                    <FormErrorMessage>{getFormError(form.errors.agree, _)}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Large fontSize={["lg", "xl"]}><Trans>Moda pembayaran:</Trans></Large>
              <PaymentMethods
                currency={currency}
                isSubmitting={isSubmitting}
                allowPayment={allowPayment}
                enabledPayments={backendEnabledPayments}
                disabledPayments={disabledPayments}
                donate={onClickPaymentMethod}
              />

              <Flex justifyContent="space-between" marginTop={6}>
                <Flex flexDirection="column">
                  <Large fontSize={["md"]}>
                    <Trans>Jumlah Dukungan:</Trans> {formatMoney(values.amount, { currency })}
                  </Large>
                  {values.platformFee > 0 ? (
                    <Large fontSize={["sm"]}>
                      <Trans>Biaya Layanan: </Trans> {formatMoney(values.platformFee || 0, { currency })}
                    </Large>
                  ) : null}
                  {transactionFeePolicy === "TIPPER" && (
                    <Large fontSize={["sm"]}>
                      <Trans>Biaya Pembayaran (Payment Gateway):</Trans> {formatMoney(values.pgFee || 0, { currency })}
                    </Large>
                  )}
                  <Large>
                    <Trans>Total:</Trans> {formatMoney(values.amountToPay || values.amount, { currency })}
                  </Large>
                  <Text color="red.500" fontSize="md">{getFormError(errors.payment_type, _)}</Text>
                </Flex>
                <Flex>
                  <Button
                    marginBottom={0}
                    disabled={!allowPayment || !selectedPaymentMethod}
                    data-testid="buy-button"
                    backgroundColor={`saweria-yellow.${allowPayment && selectedPaymentMethod ? "normal" : "dark"}`}
                    type="button"
                    label={t`Kirim Dukungan`}
                    onClick={handleBuy}
                  />
                </Flex>
              </Flex>

              <Modal isOpen={emailConfirmOpen} onClose={() => setEmailConfirmOpen(false)} isCentered>
                <ModalOverlay />
                <ModalContent bg="transparent" boxShadow="none">
                  <Dialog p="24px" display="flex" flexDirection="column" gap="24px">
                    <Flex direction="column" align="center" gap="26px">
                      <ChakraImage src={CapyAware.src} alt="" w="125px" h="125px" />
                      <Flex direction="column" align="center" gap="12px" w="100%">
                        <Text color="black" fontSize="20px" fontFamily="Comfortaa" lineHeight="24px">
                          <Trans>Periksa Kembali Email-mu</Trans>
                        </Text>
                        <Text textAlign="center" color="black" fontSize="16px" fontFamily="IBM Plex Mono" lineHeight="24px">
                          <Trans>Pastikan emailmu benar untuk menerima kode dan link mini games.</Trans>
                        </Text>
                        {editingEmail ? (
                          <Field name="email">
                            {({ field, form }) => (
                              <FormControl w="100%" isInvalid={form.errors.email}>
                                <Input
                                  {...field}
                                  autoFocus
                                  type="email"
                                  data-testid="confirm-email-input"
                                  placeholder={_(msg`budi@saweria.co`)}
                                  bg="white"
                                />
                                <FormErrorMessage>{getFormError(form.errors.email, _)}</FormErrorMessage>
                              </FormControl>
                            )}
                          </Field>
                        ) : (
                          <Flex px="16px" py="8px" bg="white" borderRadius="6px" align="center" gap="16px">
                            <Text color="black" fontSize="16px" fontFamily="IBM Plex Mono" fontWeight="700" lineHeight="24px">
                              {values.email}
                            </Text>
                            <Icon
                              as={FaEdit}
                              boxSize="24px"
                              color="#3182CE"
                              cursor="pointer"
                              aria-label={_(t`Ubah email`)}
                              onClick={() => setEditingEmail(true)}
                            />
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                    <Flex gap="16px">
                      <Button
                        type="button"
                        marginBottom={0}
                        w="100%"
                        backgroundColor="white"
                        label={t`Kembali`}
                        onClick={() => setEmailConfirmOpen(false)}
                      />
                      <Button
                        type="button"
                        marginBottom={0}
                        w="100%"
                        backgroundColor="saweria-green.normal"
                        disabled={Boolean(errors.email) || !values.email?.trim()}
                        label={t`Lanjutkan`}
                        onClick={handleConfirmEmail}
                      />
                    </Flex>
                  </Dialog>
                </ModalContent>
              </Modal>
            </Form>
          );
        }}
      </Formik>
    </PaymentMethodSelectorProvider>
  );
};

export { MiniGameBuyForm };
